import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import ModerationAutoIcon from "@/assets/svg/moderation-auto.svg";
import Loader from "@/components/Loader";
import Table from "@/components/Table";
import Toggle from "@/components/Toggle";
import Filters from "@/scenes/broadcast/moderation/components/Filters";
import Header from "@/scenes/broadcast/moderation/components/Header";
import SettingsModal from "@/scenes/broadcast/moderation/components/SettingsModal";
import MissionModal from "@/scenes/broadcast/moderation/modal";
import api from "@/services/api";
import { captureError } from "@/services/error";
import { compactMissionFilters } from "@/services/mission";
import useStore from "@/services/store";
import MissionItem from "./components/MissionItem";

const Moderation = () => {
  const { publisher } = useStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    size: 25,
    status: searchParams.get("status") || null,
    comment: searchParams.get("comment") || null,
    publisherIds: searchParams.has("publisherIds") ? searchParams.get("publisherIds").split(",") : [],
    organizationIds: searchParams.has("organizationIds") ? searchParams.get("organizationIds").split(",") : [],
    department: searchParams.get("department") || null,
    cities: searchParams.has("cities") ? searchParams.get("cities").split(",") : [],
    activity: searchParams.get("activity") || null,
    domain: searchParams.get("domain") || null,
    search: searchParams.get("search") || null,
  });
  const [reloadFilters, setReloadFilters] = useState(false);
  const [data, setData] = useState([]);
  const [sort, setSort] = useState("");
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);
  const [history, setHistory] = useState();

  useEffect(() => {
    if (!publisher.moderator) {
      navigate("/broadcast");
    }
  }, [publisher, navigate]);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const query = {
          publisherIds: filters.publisherIds || undefined,
          moderatorId: publisher.id || undefined,
          status: filters.status || undefined,
          comment: filters.comment || undefined,
          cities: filters.cities || undefined,
          domain: filters.domain || undefined,
          department: filters.department || undefined,
          organizationIds: filters.organizationIds || undefined,
          activity: filters.activity || undefined,
          search: filters.search || undefined,
          from: (filters.page - 1) * filters.size,
          size: size,
          sort: sort || undefined,
        };

        if (query.page > 1) {
          query.from = (query.page - 1) * query.size;
        }

        const resM = await api.post("/moderation/search", query);

        if (!resM.ok) {
          throw resM;
        }
        setData(resM.data);
        setTotal(resM.total);

        const newSearchParams = new URLSearchParams();
        Object.entries(compactMissionFilters(filters)).forEach(([key, value]) => newSearchParams.append(key, value));
        if (searchParams.has("mission")) {
          newSearchParams.set("mission", searchParams.get("mission"));
        }
        setSearchParams(newSearchParams);
      } catch (error) {
        captureError(error, { extra: { filters } });
      }
      setLoading(false);
    };
    fetchData();
  }, [filters, sort, size]);

  const fetchHistory = async () => {
    try {
      const res = await api.post("/moderation/search-history", { moderatorId: publisher.id });
      if (!res.ok) {
        throw res;
      }
      setHistory(res.data);
    } catch (error) {
      captureError(error, { extra: { publisherId: publisher.id } });
    }
  };

  const handleMissionUpdate = (updates) => {
    const list = Array.isArray(updates) ? updates : updates ? [updates] : [];
    if (!list.length) {
      return;
    }
    setData((prev) => {
      return prev.map((mission) => {
        const updated = list.find((u) => u.id === mission.id);
        return updated ? { ...mission, ...updated } : mission;
      });
    });
  };

  const handlePageChange = (page) => {
    setFilters({ ...filters, page });
    window.scrollTo({ top: 500, behavior: "smooth" });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelected(data.map((m) => m.id));
    } else {
      setSelected([]);
    }
  };

  if (!history) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-12 py-4 sm:py-12">
      <title>API Engagement - Modération - Diffuser des missions</title>
      <MissionModal
        onChange={(updates) => {
          handleMissionUpdate(updates);
          fetchHistory();
        }}
      />
      <div className="mx-4 flex flex-col gap-4 sm:mx-12 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <img src={ModerationAutoIcon} className="h-14 w-14 shrink-0" alt="" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-black">Modération automatique</h2>
        </div>

        <div className="pt-4">
          <div className="flex items-center gap-4">
            <Toggle aria-label="Activer la modération automatique" value={true} />
            <span className="ml-2 font-semibold">Activer la modération automatique</span>
          </div>
          <div className="flex items-center justify-end">
            <SettingsModal />
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-12">
        <div className="h-px w-full bg-gray-900" />
      </div>

      <Filters filters={filters} onChange={(next) => setFilters({ ...filters, ...next, page: 1 })} searchParams={searchParams} reload={reloadFilters} />
      <div className="px-4 sm:px-12">
        <div className="h-px w-full bg-gray-900" />
      </div>
      <Header
        total={total}
        data={data}
        size={size}
        sort={sort}
        selected={selected}
        onSize={(s) => {
          setSize(s);
          setFilters({ ...filters, page: 1 });
        }}
        onSort={setSort}
        onSelect={setSelected}
        onChangeMany={(updates) => {
          handleMissionUpdate(updates);
          setReloadFilters((prev) => !prev);
          fetchHistory();
        }}
      />

      <div className="mx-4 overflow-x-auto sm:mx-12">
        <Table
          caption="Missions en modération"
          header={[
            {
              title: (
                <div className="flex items-center">
                  <label className="flex w-14 items-center">
                    <span className="sr-only">Sélectionner toutes les missions</span>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selected.length === data.length && data.length > 0}
                      onChange={handleSelectAll}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selected.length > 0 && selected.length < data.length;
                        }
                      }}
                    />
                  </label>
                  <h3 className="text-sm font-semibold">Mission</h3>
                </div>
              ),
              width: "40%",
            },
            { title: "Organisation" },
            { title: "Actions", width: "35%" },
          ]}
          total={total}
          loading={loading}
          page={filters.page}
          pageSize={size}
          onPageChange={handlePageChange}
        >
          {data.map((item, i) => (
            <tr key={i} className={`${selected.includes(item.id) ? "bg-blue-france-925" : ""} table-row h-48`}>
              <MissionItem
                data={item}
                history={history.organization[item.missionOrganizationName] || { ACCEPTED: 0, REFUSED: 0 }}
                selected={selected.includes(item.id)}
                onChange={(values) => {
                  handleMissionUpdate(values);
                  fetchHistory();
                  setReloadFilters((prev) => !prev);
                }}
                onChangeMany={(updates) => {
                  handleMissionUpdate(updates);
                  fetchHistory();
                  setReloadFilters(!reloadFilters);
                }}
                onSelect={() => setSelected(selected.includes(item.id) ? selected.filter((id) => id !== item.id) : [...selected, item.id])}
                onFilter={(v) => setFilters({ ...filters, organizationIds: [v], page: 1 })}
              />
            </tr>
          ))}
        </Table>
      </div>
    </div>
  );
};

export default Moderation;
